// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Documents CRUD
// Safety procedures, contracts, FEMA docs, maps — clickable detail view
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, FileButton,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconExternalLink, IconUpload, IconFile, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { DebrisDocument } from '../../types/debris';
import { TABLES, FIELDS, WORKER_URL, updateRecord } from '../../lib/api-switch';

const CATEGORY_OPTIONS = [
  { value: 'safety', label: 'Safety' },
  { value: 'procedures', label: 'Procedures' },
  { value: 'maps', label: 'Maps' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'fema', label: 'FEMA' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
  safety: 'red', procedures: 'blue', maps: 'green',
  contracts: 'violet', fema: 'orange', other: 'gray',
};

const EMPTY: Partial<DebrisDocument> = {
  title: '', category: '', description: '', fileUrl: '',
  uploadedBy: '', visibleTo: '[]', status: 'active',
};

// Proxy Ignatius file URLs through the worker for auth
function proxyUrl(url: string): string {
  if (!url) return '';
  if (url.includes('api.ignatius.io')) {
    return `${WORKER_URL}/api/ignatius${url.replace(/^https?:\/\/api\.ignatius\.io/, '')}`;
  }
  return url;
}

export default function Documents() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident, user } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    documents, loadingDocs,
    loadDocuments, createDocument, updateDocument, deleteDocument,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisDocument | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisDocument | null>(null);
  const [viewTarget, setViewTarget] = useState<DebrisDocument | null>(null);
  const [form, setForm] = useState<Partial<DebrisDocument>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const iDocs = documents;

  const filtered = useMemo(() => {
    let list = iDocs;
    if (catFilter && catFilter !== 'all') list = list.filter(d => d.category === catFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.title.toLowerCase().includes(q) || d.description.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => a.title.localeCompare(b.title));
  }, [iDocs, catFilter, search]);

  const setField = (key: keyof DebrisDocument, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, uploadedBy: user?.email ?? '' });
    setModalOpen(true);
  };
  const openEdit = (d: DebrisDocument) => { setEditTarget(d); setForm({ ...d }); setModalOpen(true); };

  // File upload handler
  const handleFileUpload = async (file: File | null, docId?: number) => {
    if (!file || !docId) return;
    setUploading(true);
    try {
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // strip data URL prefix
        };
        reader.readAsDataURL(file);
      });

      const FD = FIELDS.debrisDocuments;
      await updateRecord(TABLES.debrisDocuments, docId, [[FD.fileUrl, base64]]);
      await loadDocuments();
      notifications.show({ message: `File "${file.name}" uploaded`, color: 'green' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Upload failed', color: 'red' });
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.title?.trim()) {
      notifications.show({ message: 'Title is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateDocument(editTarget.id, payload);
        notifications.show({ message: 'Document updated', color: 'green' });
      } else {
        const created = await createDocument(payload);
        notifications.show({ message: 'Document created', color: 'green' });
        // Return the new doc ID for file upload
        return created;
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteDocument(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.title} deleted`, color: 'gray' });
      setDeleteTarget(null);
      if (viewTarget?.id === deleteTarget.id) setViewTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const catLabel = (cat: string) => CATEGORY_OPTIONS.find(c => c.value === cat)?.label ?? cat ?? '-';

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Driver Resources</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingDocs} onClick={() => loadDocuments()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Document</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search title, description..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
          value={catFilter ?? 'all'} onChange={v => setCatFilter(v)} size="xs" w={180} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} document{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Title</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Uploaded By</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingDocs ? 'Loading...' : 'No documents found'}
                </td></tr>
              )}
              {filtered.map(d => (
                <tr key={d.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                  onClick={() => setViewTarget(d)}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                    <Group gap="xs">
                      <IconFile size={14} />
                      {d.title || '-'}
                    </Group>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={CATEGORY_COLORS[d.category] ?? 'gray'}>
                      {catLabel(d.category)}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 300 }}>
                    <Text size="xs" lineClamp={2}>{d.description || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}><Text size="xs">{d.uploadedBy || '-'}</Text></td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {canEdit && (
                    <Group gap={4} justify="flex-end" onClick={e => e.stopPropagation()}>
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(d)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(d)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Document Detail Modal */}
      <Modal opened={!!viewTarget} onClose={() => setViewTarget(null)}
        title="Document Detail" centered size="lg">
        {viewTarget && (
          <Stack>
            <Group justify="space-between">
              <div>
                <Text size="lg" fw={700}>{viewTarget.title}</Text>
                <Badge mt={4} color={CATEGORY_COLORS[viewTarget.category] ?? 'gray'}>
                  {catLabel(viewTarget.category)}
                </Badge>
              </div>
              {canEdit && (
              <Group gap="xs">
                <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
                  onClick={() => { setViewTarget(null); openEdit(viewTarget); }}>Edit</Button>
                <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={14} />}
                  onClick={() => setDeleteTarget(viewTarget)}>Delete</Button>
              </Group>
              )}
            </Group>

            {viewTarget.description && (
              <Paper p="sm" radius="md" style={{ background: 'var(--mantine-color-gray-0)' }}>
                <Text size="sm">{viewTarget.description}</Text>
              </Paper>
            )}

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Uploaded By</Text>
                <Text size="sm">{viewTarget.uploadedBy || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>Status</Text>
                <Badge size="sm">{viewTarget.status || 'active'}</Badge>
              </div>
            </SimpleGrid>

            {/* File section */}
            <Paper p="md" radius="md" withBorder>
              <Text size="sm" fw={600} mb="sm">Attached File</Text>
              {viewTarget.fileUrl ? (
                <Group gap="sm">
                  <IconFile size={20} />
                  <Text size="sm" style={{ flex: 1 }}>{viewTarget.fileUrl.split('/').pop() || 'Document file'}</Text>
                  <Button size="xs" variant="light" leftSection={<IconExternalLink size={14} />}
                    component="a" href={proxyUrl(viewTarget.fileUrl)} target="_blank" rel="noopener">
                    Open
                  </Button>
                  <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                    component="a" href={proxyUrl(viewTarget.fileUrl)} target="_blank" download>
                    Download
                  </Button>
                </Group>
              ) : (
                <Stack align="center" gap="xs" py="md">
                  <Text size="sm" c="dimmed">No file attached</Text>
                  {canEdit && (
                  <FileButton onChange={f => handleFileUpload(f, viewTarget.id)}
                    accept=".pdf,.docx,.xlsx,.pptx,.zip,.png,.jpg,.jpeg">
                    {(props) => (
                      <Button {...props} size="xs" variant="light" leftSection={<IconUpload size={14} />}
                        loading={uploading}>
                        Upload File
                      </Button>
                    )}
                  </FileButton>
                  )}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}
      </Modal>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Document' : 'Add Document'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Title" required value={form.title ?? ''}
              onChange={e => setField('title', e.currentTarget.value)} />
            <Select label="Category" data={CATEGORY_OPTIONS} value={form.category || null}
              onChange={v => setField('category', v ?? '')} clearable />
            <TextInput label="File URL (optional)" value={form.fileUrl ?? ''}
              onChange={e => setField('fileUrl', e.currentTarget.value)} placeholder="https://... or upload after creating" />
            <TextInput label="Uploaded By" value={form.uploadedBy ?? ''}
              onChange={e => setField('uploadedBy', e.currentTarget.value)} />
          </SimpleGrid>
          <Textarea label="Description" value={form.description ?? ''}
            onChange={e => setField('description', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Document'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Document" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.title}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
